import { LiveGenreIdSchema, LiveId, LiveIdSchema, LiveTaskId, LiveTaskIdSchema, LiveTokenSchema, SelectionParticipantIdSchema, SpaceKeySchema, StreamNameSchema, UserIdSchema } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { z } from "zod/v4"
import { CreatedAt, UpdatedAt } from "./system"
import { SpaceUserStatus } from "./space-user"
import { UserDenormalizedWithLook } from "./user"
import { TagsOptional } from "../common"

export const LiveStatusValue = z.union([
  z.literal("starting"),
  z.literal("started"),
  z.literal("stopping"),
  z.literal("stopped"),
]).describe("ライブの開始・終了状態。")

export type LiveStatusValue = z.infer<typeof LiveStatusValue>

export type UpdateLiveStatus = {
  task_id: LiveTaskId,
  status: "started",
} | {
  status: "stopping" | "stopped",
}

export const LiveStatus = z.union([
  z.object({
    status: z.literal("starting"),
    task_id: z.null(),
  }),
  z.object({
    status: z.union([z.literal("started"), z.literal("stopping"), z.literal("stopped")]),
    task_id: LiveTaskIdSchema.nullable()
  })
])

/**
 * ライブに設定するタグ。
 * https://www.notion.so/popopo-inc/ON-AIR-ea03bae39035443181ff48606baaf10a#d853e0b7ef004e23b054f473ce8c0560
 */
export const LiveTags = TagsOptional.describe("ライブタグ")

export const LatestLiveCount = z.object({
  current: z.number().describe("現在の視聴者数"),
  overall: z.number().describe("累計視聴者数"),
  power: z.number().optional().describe("パワー送信の累計値"),
  current_max: z.number().optional().describe("同時接続最大数"),
}).describe("最新の視聴者数情報")

export const LiveDocumentMeta = z.intersection(
  LiveStatus,
  z.object({
    user_id: UserIdSchema, // for collection group query
    space_key: SpaceKeySchema, // for collection group query
    users: z.array(UserIdSchema).describe("このライブ中にスペースで online になったユーザーIDの一覧。思い出の表示のために利用する。"),
    selected_talk_participant: z.intersection(SpaceUserStatus, z.object({
      id: SelectionParticipantIdSchema,
      user: UserDenormalizedWithLook,
    })).nullable(),
    token: LiveTokenSchema,
    stream_name: StreamNameSchema,
    genre_id: LiveGenreIdSchema,
    tags: LiveTags,
    can_enter: z.boolean(),
    allow_silent_join: z.boolean().optional().describe("『静かに参加』を許可するかどうか。trueの場合、視聴者は名前を表示せずにライブに参加できる。デフォルトはfalse（不許可）。"),
    latest_count: LatestLiveCount,
    count_next_update_scheduled_at: z.number().nullable().describe("次回の視聴者数情報更新予定時刻。nullの場合は更新しない。バックエンド参照用で、フロントで使うことは意図していない"),
    // TODO: dev/stageリリースからしばらくしたらoptionalを外す
    thumbnails_next_update_scheduled_at: z.number().nullable().optional().describe("次回のスペースサムネイル更新予定時刻。nullの場合は更新しない。バックエンド参照用で、フロントで使うことは意図していない"),
    reaction_aggregation_next_scheduled_at: z.number().nullable().optional().describe("次回のリアクション集約予定時刻。nullの場合は集約しない。バックエンド参照用で、フロントで使うことは意図していない"),
    reaction_aggregation_last_processed_at: z.number().nullable().optional().describe("前回のリアクション集約処理時刻。カーソルとして使用し、この時刻より後に作成されたリアクションを集約対象とする。バックエンド参照用"),
    power_count_next_sync_scheduled_at: z.number().nullable().optional().describe("次回のパワーカウントシャード集計予定時刻。nullの場合は集計しない。バックエンド参照用で、フロントで使うことは意図していない"),
    owner_last_connected_at: z.number().describe("ライブのスペースオーナーが最後に接続した時刻。バックエンド参照用で、フロントで使うことは意図していないので同等の値がほしい場合はspaceから取得する。"),
  })
)

export const LiveDocumentBody = z.intersection(
  LiveDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type LiveDocumentBody = z.infer<typeof LiveDocumentBody>

export const LiveDocument = z.intersection(
  LiveDocumentBody,
  z.object({ id: LiveIdSchema }),
)

export type LiveDocument = z.infer<typeof LiveDocument>

/**
 * 非正規化されたライブ情報。
 * ライブ削除後もデータを参照可能にするために、他のドキュメントに埋め込んで使用する。
 */
export const LiveDenormalized = z.object({
  id: LiveIdSchema.describe("ライブID。"),
  user_id: UserIdSchema.describe("配信者のユーザーID。"),
  space_key: SpaceKeySchema.describe("ライブが行われたスペースキー。"),
  genre_id: LiveGenreIdSchema.describe("ライブのジャンルID。"),
  tags: LiveTags,
  users: z.array(UserIdSchema).describe("このライブ中にスペースで online になったユーザーIDの一覧。"),
  can_enter: z.boolean().describe("ライブへの入室可否。"),
  allow_silent_join: z.boolean().optional().describe("『静かに参加』を許可するかどうか。"),
  latest_count: LatestLiveCount.describe("最新の視聴者数情報。"),
})

export type LiveDenormalized = z.infer<typeof LiveDenormalized>

export class Lives {

  static toLiveDenormalized(live: LiveDocument): LiveDenormalized {
    return {
      id: live.id,
      user_id: live.user_id,
      space_key: live.space_key,
      genre_id: live.genre_id,
      tags: live.tags,
      users: live.users,
      can_enter: live.can_enter,
      allow_silent_join: live.allow_silent_join,
      latest_count: live.latest_count,
    }
  }
}

export class LiveDocuments {

  public static readonly MAX_COUNT = 1

  // 視聴参加システムコメントを書き込む同時視聴者数の上限
  // https://www.notion.so/popopo-inc/30a5e03d57e28082987acac266ab4ceb
  public static readonly VIEWER_JOIN_COMMENT_THRESHOLD = 30

  static toDocumentData(live: LiveDocument): object {
    return {
      ...live,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): LiveDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: LiveId(doc.id),
      user_id: data["user_id"],
      space_key: data["space_key"],
      task_id: data["task_id"],
      status: data["status"],
      users: data["users"],
      selected_talk_participant: data["selected_talk_participant"] ?? null,
      token: data["token"],
      stream_name: data["stream_name"] ?? data["space_key"] + "_" + doc.id + "_" + data["token"],
      genre_id: data["genre_id"] ?? "vuW1bpwgj4sCeoVL9pS8" /* 一定期間過ぎたらこのロジックは不要なので消す */,
      tags: data["tags"],
      can_enter: data["can_enter"] ?? true,
      allow_silent_join: data["allow_silent_join"] ?? false,
      latest_count: data["latest_count"] == null ? {
        // 過去データの互換用
        current: 0,
        overall: 0,
      } : {
        current: data["latest_count"]["current"],
        overall: data["latest_count"]["overall"],
        power: data["latest_count"]["power"],
        current_max: data["latest_count"]["current_max"]
      },
      count_next_update_scheduled_at: data["count_next_update_scheduled_at"],
      thumbnails_next_update_scheduled_at: data["thumbnails_next_update_scheduled_at"],
      reaction_aggregation_next_scheduled_at: data["reaction_aggregation_next_scheduled_at"],
      reaction_aggregation_last_processed_at: data["reaction_aggregation_last_processed_at"],
      power_count_next_sync_scheduled_at: data["power_count_next_sync_scheduled_at"],
      owner_last_connected_at: data["owner_last_connected_at"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}

export const CreateLiveRequest = z.object({
  genreId: LiveGenreIdSchema,
  tags: LiveTags,
  canEnter: z.boolean(),
  allowSilentJoin: z.boolean().optional(),
})

export type CreateLiveRequest = z.infer<typeof CreateLiveRequest>

export const UpdateLiveRequest = z.object({
  genreId: LiveGenreIdSchema.optional(),
  tags: LiveTags.optional(),
  canEnter: z.boolean().optional(),
  allowSilentJoin: z.boolean().optional(),
})

export type UpdateLiveRequest = z.infer<typeof UpdateLiveRequest>

// 管理者用リクエスト（現時点では UpdateLiveRequest と同一）
export const ManageUpdateLiveRequest = UpdateLiveRequest

export type ManageUpdateLiveRequest = z.infer<typeof ManageUpdateLiveRequest>
